import { useTranslation } from "react-i18next";
import { AlarmClock, Clock, DoorOpen, LogIn, LogOut, Timer } from "lucide-react";
import { NeuCard } from "../../components/neu/Neu";
import { formatTime } from "../../lib/format";
import { ShiftProgress } from "./ShiftProgress";

const fmt = (iso) => formatTime(iso);

// ظل 3D للكارت الخارجي فقط (نفس مواصفات StatCard): طبقة خارجية عميقة + برُوز داخلي علوي + انحناء داخلي سفلي.
const TODAY_3D = {
  boxShadow:
    "0 14px 28px rgba(0,0,0,0.32), 0 8px 12px rgba(0,0,0,0.20),"
    + " inset 0 1.5px 0 rgba(255,255,255,0.28), inset 0 -4px 10px rgba(0,0,0,0.18)",
};

const Metric = ({ icon: Icon, label, value, hint, color }) => (
  <div className="flex items-center gap-2 rounded-3xl bg-background px-2 py-2 shadow-neu-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-neu">
    <span
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-neu-sm ${color}`}
    >
      <Icon className="h-6 w-6" strokeWidth={2.4} />
    </span>
    <div className="min-w-0">
      <p className="truncate text-xs font-bold text-muted-foreground">{label}</p>
      <p className="truncate font-mono text-xl font-black leading-tight">{value}</p>
      {hint ? <p className="truncate font-mono text-[11px] font-semibold text-muted-foreground">{hint}</p> : null}
    </div>
  </div>
);

export const TodayCard = ({ record, branch, day }) => {
  const { t } = useTranslation();
  const mins = (v) => `${v ?? 0} ${t("app.minutes")}`;
  const metrics = [
    { key: "checkedInAt", icon: LogIn, value: fmt(record?.check_in_at), color: "bg-emerald-500", hint: branch?.work_start },
    { key: "checkedOutAt", icon: LogOut, value: fmt(record?.check_out_at), color: "bg-sky-500", hint: branch?.work_end },
    { key: "late", icon: AlarmClock, value: mins(record?.late_minutes), color: "bg-red-500" },
    {
      key: "worked",
      icon: Clock,
      value: `${(((record?.worked_minutes ?? 0) / 60) || 0).toFixed(1)} ${t("app.hours")}`,
      color: "bg-violet-600",
    },
    { key: "overtime", icon: Timer, value: mins(record?.overtime_minutes), color: "bg-amber-500" },
    { key: "earlyLeave", icon: DoorOpen, value: mins(record?.early_leave_minutes), color: "bg-teal-500" },
  ];

  return (
    <NeuCard data-testid="today-card" className="animate-rise-in p-4" style={TODAY_3D}>
      <div className="flex items-center justify-between gap-3">
        <p className="label-xs text-sm">{t("check.todayStatus")}</p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {metrics.map((m) => (
          <div key={m.key} data-testid={`today-${m.key}`}>
            <Metric icon={m.icon} label={t(`check.${m.key}`)} value={m.value} hint={m.hint} color={m.color} />
          </div>
        ))}
      </div>
      <ShiftProgress branch={branch} record={record} day={day} />
    </NeuCard>
  );
};
